import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import cn from 'classnames';
import TrustReviewSlider from '@/components/commons/TrustReviewSlider';
import TrustpilotWidget from '@/components/products/TrustpilotWidget';
import settings from '@/settings';
import StepsFeatured from './StepsFeatured';
import TrustedFeatured from './TrustedFeatured';
import Faq from './Faq';
import TextFeatured from './TextFeatured';
import PriceTable, { PriceItem } from './PriceTable';
import SellPhoneForCash from './SellPhoneForCash';
import Banner from './Banner';
import SellBusinessPhonesNotebooks from './SellBusinessPhonesNotebooks';
import Navigation, { NavigationItem } from './Navigation';
import ProductsListLayout, {
  DeviceList,
  DeviceListWithConditions,
} from './ProductsList';
import BackToTopButton from '../collections/BackToTopButton';
import { WebsiteSchema } from '../homepage/WebsiteSchema';

export type BannerData = {
  heading: string;
  description?: string;
};

type PriceListWithCondition = {
  new: Array<PriceItem>;
  used: Array<PriceItem>;
};

type Props = {
  brand: string;
  brandShort: string;
  bannerData: BannerData;
  priceTableList?: PriceItem[] | PriceListWithCondition | undefined;
  priceTableTitle?: string;
  typeFormDevicesList?: DeviceList[] | DeviceListWithConditions;
  navigationList?: NavigationItem[];
  isMainNav?: boolean;
  customSEOText?: {
    title: string;
    description: string;
  };
  title: string;
};

const defaultCustomSEOText = {
  title: `Sell your Phone in ${settings.country}`,
  description: `Sell your used or as new phone with Reebelo across ${settings.country}. Whether you are based in Sydney, Melbourne, Perth, Gold Coast, Brisbane, Adelaide, Newcastle, or Canberra, you can easily sell your phones with Reebelo.`,
};

const PriceSection = ({
  priceTableList,
  brand,
  customText,
  containerClass,
}: {
  priceTableList?: PriceItem[] | PriceListWithCondition;
  brand: string;
  customText: string;
  containerClass: string;
}) => {
  if (priceTableList == null) return null;

  if (Array.isArray(priceTableList)) {
    return (
      <section className="bg-gray-500 py-10 md:py-20">
        <div className={containerClass}>
          <PriceTable
            brand={brand}
            priceList={priceTableList}
            title={`Used ${brand} ${customText} available for trade-in`}
            priceColumn={
              settings.store === 'quista' || settings.store === 'reebelo-us'
            }
          />
        </div>
      </section>
    );
  }
  const newPriceListExist = priceTableList.new && priceTableList.new.length > 0;
  const normalizedCustomText =
    customText === 'iPhone' ? 'Apple iPhone' : customText;

  return (
    <>
      <section className="bg-gray-500 py-10 md:py-20">
        <div
          className={cn(containerClass, {
            'mb-12': newPriceListExist,
          })}
        >
          <PriceTable
            brand={brand}
            priceList={priceTableList.used}
            title={`Used ${normalizedCustomText} trade-in price`}
            priceColumn={
              settings.store === 'quista' || settings.store === 'reebelo-us'
            }
          />
        </div>
        {newPriceListExist && settings.store !== 'reebelo-us' && (
          <div className={containerClass}>
            <PriceTable
              brand={brand}
              priceList={priceTableList.new}
              title={`New ${normalizedCustomText} trade-in price`}
              priceColumn={settings.store === 'quista'}
            />
          </div>
        )}
      </section>
    </>
  );
};

export default function Buyback({
  brandShort,
  bannerData,
  typeFormDevicesList,
  customSEOText = defaultCustomSEOText,
  title,
  priceTableList,
  navigationList = [],
  isMainNav = false,
}: Props) {
  const router = useRouter();

  useEffect(() => {
    if (!settings.buyback_exist) router.push('/');
  }, [router]);
  const commonContainerCss = 'reebelo-container 2xl:max-w-[70%]';

  return (
    <>
      <Head>
        <title>{customSEOText.title}</title>
        <WebsiteSchema />
        <meta name="description" content={customSEOText.description} />
      </Head>
      <BackToTopButton />
      <div>
        <section>
          <Banner
            heading={bannerData.heading}
            description={bannerData.description}
          />
        </section>
        <section>
          <div className={commonContainerCss}>
            <Navigation isMainNav={isMainNav} items={navigationList} />
          </div>
        </section>
        <section className="bg-gray-500">
          <TrustpilotWidget
            desktopAlign="center"
            mobileAlign="center"
            className="py-5 text-center text-xs xs:py-6 xs:text-sm sm:py-8 sm:text-base"
            starClassName="h-4 w-4 xs:h-5 xs:w-5"
          />
        </section>
        <section className="bg-white">
          <div className={cn(commonContainerCss)}>
            <StepsFeatured customText={title} />
          </div>
        </section>
        {settings.store === 'reebelo-au' && (
          <section className="py-10">
            <div className={commonContainerCss}>
              <SellPhoneForCash />
            </div>
          </section>
        )}
        {typeFormDevicesList && (
          <section className="py-10">
            <div className={commonContainerCss}>
              <ProductsListLayout
                devices={typeFormDevicesList}
                customText={title}
              />
            </div>
          </section>
        )}
        <section className="pt-10">
          <div
            className={cn(commonContainerCss, {
              'mb-10':
                settings.store === 'quista' || settings.store === 'reebelo-us',
            })}
          >
            <TrustedFeatured />
          </div>
        </section>
        {settings.store === 'reebelo-au' && (
          <section className="py-10">
            <div className={commonContainerCss}>
              <SellBusinessPhonesNotebooks />
            </div>
          </section>
        )}
        <section className="bg-white py-10">
          <div className={cn(commonContainerCss)}>
            <TrustReviewSlider />
          </div>
        </section>
        <section className="bg-white py-10 md:py-20">
          <div className="reebelo-container 2xl:max-w-[70%]">
            <Faq />
          </div>
        </section>
        <section>
          <div className={cn(commonContainerCss, 'py-10 md:py-20')}>
            <TextFeatured customSEOText={customSEOText} customText={title} />
          </div>
        </section>
        <PriceSection
          containerClass={commonContainerCss}
          brand={brandShort}
          priceTableList={priceTableList}
          customText={title}
        />
      </div>
    </>
  );
}
